'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon, type IconSize } from '@/ui/icons/icon/Icon'
import type { ReactNode } from 'react'
import { useState } from 'react'
import type { Placement } from 'react-aria'
import { Focusable } from 'react-aria-components'
import { Tooltip, TooltipTrigger } from '../tooltip/Tooltip.client'

export type InfoTooltipProps = UiComponentProps & {
  /** Text displayed in the tooltip */
  tooltipText: ReactNode

  /** Position of the tooltip relative to its trigger */
  placement?: Placement

  /** Size of the default info icon trigger */
  iconSize?: IconSize

  /** Disables tooltip interactions */
  isDisabled?: boolean

  /** Custom trigger. If omitted, an info icon button is rendered. */
  children?: ReactNode
}

export function InfoTooltip({
  id,
  tooltipText,
  placement = 'top',
  iconSize = 'sm',
  isDisabled,
  children,
  className,
  'data-testid': dataTestId,
}: InfoTooltipProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const { t } = useTranslation(['infoTooltip'])
  const rootTestId = dataTestId ?? 'info-tooltip'

  const openTooltip = () => {
    if (!isDisabled) {
      setIsTooltipOpen(true)
    }
  }

  return (
    <TooltipTrigger
      isDisabled={isDisabled}
      isOpen={isTooltipOpen}
      onOpenChange={setIsTooltipOpen}
      delay={0}
      closeDelay={0}
      data-testid={rootTestId}
    >
      {children ? (
        <Focusable>
          <span role="button" tabIndex={0} onPointerDown={openTooltip} onMouseEnter={openTooltip}>
            {children}
          </span>
        </Focusable>
      ) : (
        <Button
          size="md"
          variant="quaternary"
          onPress={openTooltip}
          onPointerDown={openTooltip}
          className="flex place-items-center"
          data-testid={`${rootTestId}/trigger`}
          iconLeft={<Icon.Info size={iconSize} />}
          isRound
          intent={INTENT_SAME_AS_WEB}
        >
          {t('infoTooltip:label')}
        </Button>
      )}

      <Tooltip
        id={id}
        data-testid={`${rootTestId}/content`}
        className={className}
        placement={placement}
      >
        {tooltipText}
      </Tooltip>
    </TooltipTrigger>
  )
}
